/**
 * Meta OAuth callback — invisible pass-through (#4173 Chunk 11).
 *
 * Mirrors HubSpot's UX: after Meta auth, the user should land on ``/settings/integrations`` with the
 * connection live — no intermediate "Meta Ads Integration → Back to Settings" page. This route is
 * still needed because ``META_REDIRECT_URI`` (registered in the Meta App dashboard + the CD workflow)
 * currently points here; it cannot move to ``/settings/integrations`` until the dashboard entry is
 * updated. So the route stays as a transparent processor: POST the code, navigate back, render
 * nothing visible. Once we change ``META_REDIRECT_URI`` upstream we can delete this file entirely
 * and fold the handling into the integrations layout like ``createHubSpotIntegration`` already does.
 *
 * Multi-account selection that used to live here moved to an auto-opened modal on the integrations
 * page itself (see ``IntegrationSettings``); the user sees one continuous surface.
 */

import { useCallback, useEffect, useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';

import { createMetaIntegration } from '@/lib/apis';
import { useToast } from '@/hooks/useToast';
import { classifyMetaCallback, returnPathFromMetaState } from '@/lib/meta';
import type { MetaCallbackRequest } from '@/lib/types';

export const Route = createFileRoute('/_authenticated/_layout/settings/_layout/integrations/meta/callback')({
  component: MetaCallback,
});

function MetaCallback() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const { toast } = useToast();
  // Ref, not state: the OAuth `code` is single-use, and React StrictMode double-invokes effects in dev — a
  // state flag isn't updated synchronously between the two invocations, so the second one would re-POST the
  // (now-consumed) code and get a 400. A ref is mutated synchronously, so it actually blocks the re-run.
  const processedRef = useRef(false);

  // Land the user back where they connected from (#5907) — the Connect button rode the origin
  // path on the OAuth state, and Meta echoes `state` back on cancel/error redirects too. EVERY
  // outcome returns there (#6163 P2, #6182): a failure toast belongs on the page the user left,
  // where the Connect button is, not on a generic integrations page they may not have come from.
  // Falls back to /settings/integrations (which auto-opens the ad-account picker) when no path
  // was carried. The carried path may include a hash (e.g. the picker's active platform tab) —
  // TanStack takes it as a separate param.
  const navigateBack = useCallback(() => {
    const returnPath = returnPathFromMetaState(new URLSearchParams(window.location.search).get('state'));
    const [pathname, hash] = (returnPath ?? '/settings/integrations').split('#');
    navigate({ to: pathname, hash, replace: true });
  }, [navigate]);

  const createIntegrationMutation = useMutation({
    mutationFn: (data: MetaCallbackRequest) => createMetaIntegration(data),
    onSuccess: (data) => {
      if (data.success && data.integration_status) {
        // Real connect completion (#6163 P1-11): the funnel START is 'meta_connect_started' at
        // OAuth-URL mint; this is where the integration is actually live, so 'meta_connected' fires here.
        posthog?.capture('meta_connected');
        queryClient.invalidateQueries({ queryKey: ['meta', 'integration', 'status'] });
      } else {
        toast({
          title: 'Integration Failed',
          description: data.message || 'Failed to complete Meta integration',
          variant: 'destructive',
        });
      }
      navigateBack();
    },
    onError: (error) => {
      toast({
        title: 'Integration Failed',
        description: error.message || 'Failed to complete Meta integration',
        variant: 'destructive',
      });
      navigateBack();
    },
  });

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const outcome = classifyMetaCallback(window.location.search);

    if (outcome.kind === 'success') {
      createIntegrationMutation.mutate({ code: outcome.code, state: outcome.state });
      return;
    }

    toast({
      title: outcome.kind === 'error' ? 'Authorization Cancelled' : 'Invalid Callback',
      description: outcome.message,
      variant: 'destructive',
    });
    navigateBack();
  }, [createIntegrationMutation, navigateBack, toast]);

  // Render nothing — the page exists only to process the OAuth response and redirect. Brief blank
  // flash is the same UX users get on the HubSpot callback path.
  return null;
}
